import { useState, useEffect } from 'react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import { Card, PageHeader } from '../components/UI';
import { fmt } from '../utils/helpers';

const PERIODS = [
  { value: '7d',  label: '7D'  },
  { value: '30d', label: '30D' },
  { value: '90d', label: '90D' },
  { value: '1y',  label: '1Y'  },
];

const TT = {
  contentStyle: { background: 'var(--surface2)', border: '1px solid var(--border2)', borderRadius: 8, fontSize: 12 },
  labelStyle:   { color: 'var(--text2)' },
};

export default function Analytics() {
  const [period,   setPeriod]   = useState('30d');
  const [lodge,    setLodge]    = useState('');       // '' = all lodges
  const [lodges,   setLodges]   = useState([]);
  const [revenue,  setRevenue]  = useState(null);
  const [plans,    setPlans]    = useState([]);
  const [growth,   setGrowth]   = useState([]);
  const [loading,  setLoading]  = useState(false);
  const toast = useToast();

  // Fetch lodge list once on mount
  useEffect(() => {
    api.getLodges()
      .then(r => setLodges(r.lodges || []))
      .catch(() => {});
  }, []);

  // Refetch analytics whenever period or lodge changes
  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.getRevenueAnalytics(period, lodge),
      api.getPlanBreakdown(lodge),
      api.getUserGrowth(lodge),
    ])
      .then(([r, p, u]) => {
        setRevenue(r);
        setPlans(p.breakdown || []);
        setGrowth(u.growth   || []);
      })
      .catch(e => toast(e.message, 'error'))
      .finally(() => setLoading(false));
  }, [period, lodge]);

  const revenueData = revenue?.daily?.map(d => ({
    date:    new Date(d.date).toLocaleDateString('en-NG', { day: '2-digit', month: 'short' }),
    Revenue: parseFloat(d.revenue || 0),
  })) || [];

  const growthData = growth.map(d => ({
    date:  new Date(d.date).toLocaleDateString('en-NG', { day: '2-digit', month: 'short' }),
    Users: parseInt(d.new_users || 0),
  }));

  const planData = plans.map(p => ({
    name:    p.plan_name,
    Revenue: parseFloat(p.total_revenue || 0),
    Sold:    parseInt(p.count || 0),
  }));
  const maxRev = planData.reduce((m, p) => Math.max(m, p.Revenue), 1);

  const selectedLodgeName = lodges.find(l => l.id === lodge)?.name;

  return (
    <div className="page-enter">
      <PageHeader
        title="Analytics"
        subtitle={selectedLodgeName ? `Filtered: ${selectedLodgeName}` : 'Revenue trends and platform performance'}
        action={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>

            {/* Lodge filter */}
            <div style={{ position: 'relative' }}>
              <select
                value={lodge}
                onChange={e => setLodge(e.target.value)}
                style={{
                  padding: '6px 28px 6px 10px',
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  background: lodge ? 'var(--accent-dim)' : 'var(--surface)',
                  color: lodge ? 'var(--accent)' : 'var(--text2)',
                  fontSize: 12,
                  fontWeight: lodge ? 700 : 400,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  outline: 'none',
                  appearance: 'none',
                  minWidth: 120,
                }}
              >
                <option value="">All Lodges</option>
                {lodges.map(l => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
              {/* chevron */}
              <svg
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}
                style={{ width: 11, height: 11, position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text3)' }}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>

            {/* Period toggle */}
            <div style={{ display: 'flex', gap: 3, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 9, padding: 3 }}>
              {PERIODS.map(p => (
                <button key={p.value} onClick={() => setPeriod(p.value)}
                  style={{
                    padding: '6px 12px', borderRadius: 7, border: 'none', cursor: 'pointer',
                    fontSize: 12, fontWeight: p.value === period ? 700 : 400,
                    fontFamily: 'inherit',
                    background: p.value === period ? 'var(--accent)' : 'transparent',
                    color:      p.value === period ? '#fff' : 'var(--text2)',
                    transition: 'all 0.15s',
                  }}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        }
      />

      {/* Active lodge banner */}
      {lodge && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 14px', marginBottom: 16,
          background: 'var(--accent-dim)', border: '1px solid var(--accent)',
          borderRadius: 9, fontSize: 12,
        }}>
          <span style={{ color: 'var(--accent)', fontWeight: 600 }}>
            Showing data for: <b>{selectedLodgeName}</b>
          </span>
          <button onClick={() => setLodge('')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', fontSize: 11, fontFamily: 'inherit', fontWeight: 600 }}>
            Clear ✕
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 80, color: 'var(--text3)' }}>
          <div style={{ width: 24, height: 24, border: '2px solid var(--border2)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.6s linear infinite', margin: '0 auto 12px' }} />
          Loading analytics…
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: 12, marginBottom: 20 }}>
            {[
              { label: 'Total Revenue',     value: fmt.naira(revenue?.summary?.total_revenue),                         color: 'var(--green)'  },
              { label: 'Successful Topups', value: (revenue?.summary?.successful_topups    || 0).toLocaleString(),      color: 'var(--accent)' },
              { label: 'Failed Txns',       value: (revenue?.summary?.failed_transactions  || 0).toLocaleString(),      color: 'var(--red)'    },
            ].map(s => (
              <Card key={s.label} style={{ padding: '16px 18px' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>{s.label}</div>
                <div style={{ fontSize: 20, fontWeight: 800, fontFamily: 'Syne,sans-serif', color: s.color, wordBreak: 'break-all' }}>{s.value}</div>
              </Card>
            ))}
          </div>

          {/* Daily revenue chart */}
          <Card style={{ marginBottom: 18, padding: '18px 16px' }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 16 }}>Daily Revenue</h3>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={revenueData}>
                <defs>
                  <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#22c55e" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0}   />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--text3)' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10, fill: 'var(--text3)' }} axisLine={false} tickLine={false} tickFormatter={v => `₦${(v / 1000).toFixed(0)}k`} width={45} />
                <Tooltip {...TT} formatter={v => [fmt.naira(v), 'Revenue']} />
                <Area type="monotone" dataKey="Revenue" stroke="#22c55e" strokeWidth={2} fill="url(#g)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </Card>

          {/* User signups chart */}
          <Card style={{ marginBottom: 18, padding: '18px 16px' }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 16 }}>
              User Signups (30d){selectedLodgeName ? ` — ${selectedLodgeName}` : ''}
            </h3>
            <ResponsiveContainer width="100%" height={140}>
              <BarChart data={growthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--text3)' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10, fill: 'var(--text3)' }} axisLine={false} tickLine={false} />
                <Tooltip {...TT} />
                <Bar dataKey="Users" fill="#4f8ef7" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          {/* Revenue by plan */}
          <Card style={{ padding: '18px 16px' }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 16 }}>Revenue by Plan</h3>
            {planData.length === 0 ? (
              <div style={{ color: 'var(--text3)', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No data for this filter.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {planData.map((p, i) => {
                  const pct = (p.Revenue / maxRev) * 100;
                  return (
                    <div key={i}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, fontSize: 12, flexWrap: 'wrap', gap: 4 }}>
                        <code style={{ fontFamily: 'Geist Mono,monospace', color: 'var(--accent)', fontSize: 11 }}>{p.name}</code>
                        <span>{fmt.naira(p.Revenue)} <span style={{ color: 'var(--text3)' }}>({p.Sold} sold)</span></span>
                      </div>
                      <div style={{ height: 7, background: 'var(--surface2)', borderRadius: 99, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: `hsl(${210 + i * 28},75%,58%)`, borderRadius: 99, transition: 'width 0.8s ease' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}